import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface FieldDefinition {
  id: string;
  fieldName: string;
  displayName: string;
  fieldType: string;
  isRequired: boolean;
}

interface Props {
  entityType: string;
  entityId: string;
  /**
   * Heading for the card. Defaults to the generic "custom fields" label; pass a
   * domain one where the tenant's fields form a recognisable block (admissions,
   * underwriting, …) so the screen reads as one section instead of an appendix.
   */
  title?: string;
}

/**
 * Story 20.6 — renders custom fields for any entity. Fetches
 * definitions + current values, renders appropriate inputs, and
 * saves on blur or explicit save.
 */
export function CustomFieldsPanel({ entityType, entityId, title }: Props) {
  const { t } = useTranslation();
  const [definitions, setDefinitions] = useState<FieldDefinition[]>([]);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [defsRes, valsRes] = await Promise.all([
        apiClient.get<FieldDefinition[]>('/custom-fields/definitions', {
          params: { entityType },
        }),
        apiClient.get<Record<string, unknown>>(
          `/custom-fields/values/${entityType}/${entityId}`,
        ),
      ]);
      setDefinitions(defsRes.data);
      setValues(valsRes.data);
      setDirty(false);
    } catch {
      setDefinitions([]);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    load();
  }, [load]);

  function updateValue(fieldName: string, value: unknown) {
    setValues((prev) => ({ ...prev, [fieldName]: value }));
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      await apiClient.put(
        `/custom-fields/values/${entityType}/${entityId}`,
        values,
      );
      setDirty(false);
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  }

  if (definitions.length === 0) return null;

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {title ?? t('customFields.title')}
        </h2>
        {dirty && (
          <Button size="sm" onClick={save} disabled={saving}>
            {saving ? t('customFields.saving') : t('customFields.save')}
          </Button>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {definitions.map((def) => (
          <div key={def.id} className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              {def.displayName}
              {def.isRequired && <span className="text-red-500"> *</span>}
            </label>
            {renderInput(def, values[def.fieldName], (v) =>
              updateValue(def.fieldName, v),
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function renderInput(
  def: FieldDefinition,
  value: unknown,
  onChange: (v: unknown) => void,
) {
  switch (def.fieldType) {
    case 'BOOLEAN':
      return (
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4"
        />
      );
    case 'DATE':
      return (
        <Input
          type="date"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    case 'INTEGER':
      return (
        <Input
          type="number"
          step="1"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : null)}
        />
      );
    case 'NUMERIC':
      return (
        <Input
          type="number"
          step="0.01"
          value={(value as string) ?? ''}
          onChange={(e) =>
            onChange(e.target.value ? parseFloat(e.target.value) : null)
          }
        />
      );
    default:
      return (
        <Input
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
  }
}
